import { Card, Table, Typography } from '@mui/joy';
import React from 'react';
import { Round } from '../models/round';
import { computeParOffset, formatOffset } from '../utils/par';

interface HoleListProps {
    currentRound?: Round;
    currentPlayerId?: string;
}

const HoleList = ({ currentRound, currentPlayerId }: HoleListProps) => {
    const players = [...(currentRound?.players ?? [])].sort((a, b) => {
        if (a.id === currentPlayerId) return -1;
        if (b.id === currentPlayerId) return 1;
        return 0;
    });

    const roundHoles = currentRound?.holes ?? [];

    const holeNumbers = Array.from(new Set([
        ...roundHoles.map(h => h.number),
        ...players.flatMap(p => p.scores.map(s => s.hole)),
    ])).sort((a, b) => a - b);

    const parTotal = roundHoles.reduce((sum, h) => sum + h.par, 0);
    const hasAnyPar = roundHoles.length > 0;

    return <Card variant="outlined">
        <Table borderAxis="xBetween" sx={{ '& tr > *:not(:first-child)': { textAlign: 'right' } }}>
            <thead>
            <tr>
                <th style={{ width: '20%' }}>Hole</th>
                {hasAnyPar && <th style={{ width: '10%' }}>Par</th>}
                {players.map(player => (
                    <th key={player.id}>
                        {player.id === currentPlayerId
                            ? <Typography fontWeight="bold">{player.name} (you)</Typography>
                            : player.name}
                    </th>
                ))}
            </tr>
            </thead>
            <tbody>
            {holeNumbers.map(hole => {
                const par = roundHoles.find(h => h.number === hole)?.par;
                return (
                    <tr key={hole}>
                        <th scope="row">{hole}</th>
                        {hasAnyPar && <td>{par ?? '–'}</td>}
                        {players.map(player => {
                            const score = player.scores.find(s => s.hole === hole);
                            return <td key={player.id}>{score ? score.score : '–'}</td>;
                        })}
                    </tr>
                );
            })}
            </tbody>
            <tfoot>
            <tr>
                <th>Total</th>
                {hasAnyPar && <td>{parTotal}</td>}
                {players.map(player => {
                    const total = player.scores.reduce((acc, s) => acc + s.score, 0);
                    const offset = computeParOffset(player.scores, roundHoles);
                    return (
                        <td key={player.id}>
                            {offset !== null ? `${total} (${formatOffset(offset)})` : total}
                        </td>
                    );
                })}
            </tr>
            </tfoot>
        </Table>
    </Card>;
};

export default HoleList;
